import { useEffect, useState } from 'react';
import useToastContext from '@contexts/ToastContext/hook';
import { parseFilters } from '@utils/parseFilters';
import { findRecurringRevenue } from '@api/reports';
import {
  RecurringRevenueResponse,
  RecurringRevenueRow,
  RecurringRevenueSnapshot,
  RecurringRevenueTotals,
} from '@interfaces/reports/recurringRevenue';
import { PaymentConversionPeriod } from '@interfaces/reports/paymentConversion';

export type RecurringRevenueFiltersInput = {
  botConfigIds: number[];
  startDate: Date;
  endDate: Date;
  clientTz: string;
  /**
   * O papel pedido como ESTREITAMENTO, e opcional desde a #360.
   *
   * Ausente é o modo "Tudo o que eu alcanço": o recorte sai do `scopeByProduct` que a `platform-api`
   * resolve, e é o único que fala de acesso por grant.
   */
  userRoles?: string[];
  /** Carried only so the drill-down link can hand the period label over; not part of the request. */
  periodRange?: string;
};

export const emptySnapshot: RecurringRevenueSnapshot = {
  // NOT zero. A response that never sent the population must not be indistinguishable from one reporting an
  // empty base - that collision is what #267 kept surfacing, one screen at a time.
  activeSubscribers: null,
  mrr: 0,
  arr: 0,
  averageMonthlyTicket: 0,
  cancellationScheduled: 0,
  mrrAtRisk: 0,
};

export const emptyTotals: RecurringRevenueTotals = {
  newSubscribers: 0,
  churnedSubscribers: 0,
  newMrr: 0,
  churnedMrr: 0,
  netMrr: 0,
  churnRate: 0,
  openingSubscribers: 0,
  closingSubscribers: 0,
};

/**
 * Not priced, on purpose, when the API said nothing.
 *
 * The API deploys separately, so a response predating the priced series carries no coverage at all - and the
 * honest default is that we cannot claim the better definition. `pricedFromPeriods: false` is what the screens
 * gate on; the counts beside it are meaningless in that state and are zero rather than invented.
 */
export const emptyCoverage: RecurringRevenueResponse['coverage'] = {
  pricedFromPeriods: false,
  opening: { covered: 0, total: 0 },
  // `total: null`, not 0: an absent coverage must stay distinguishable from one reporting an empty base, or the
  // only population a partial payload did supply has to be discarded to stay safe. See #267.
  current: { covered: 0, total: null },
};

export const emptyResponse: RecurringRevenueResponse = {
  snapshot: emptySnapshot,
  coverage: emptyCoverage,
  rows: [],
  totals: emptyTotals,
};

/**
 * Fills in every field a row can carry.
 *
 * The API is deployed separately from this screen, so a response predating a new field would otherwise put
 * `undefined` through a formatter and blank a column that has data beside it. Degrading to a zero is
 * readable; a blank cell in a table of numbers is not.
 */
const normaliseRow = (
  row: Partial<RecurringRevenueRow>,
): RecurringRevenueRow => ({
  period: row.period ?? '',
  activeSubscribers: row.activeSubscribers ?? 0,
  newSubscribers: row.newSubscribers ?? 0,
  churnedSubscribers: row.churnedSubscribers ?? 0,
  churnRate: row.churnRate ?? 0,
  mrr: row.mrr ?? 0,
  newMrr: row.newMrr ?? 0,
  churnedMrr: row.churnedMrr ?? 0,
  netMrr: row.netMrr ?? 0,
  // Falls back to the whole bucket rather than to zero, and that mirrors what the API itself does on its legacy
  // path: there, every counted subscription carries a price by construction. Zero would render as "nothing
  // covered" and alarm about a state that does not exist.
  coveredSubscribers: row.coveredSubscribers ?? row.activeSubscribers ?? 0,
});

/**
 * Loads the recurring base: one request, no comparison pair.
 *
 * Unlike paymentConversion there is no second call for a previous period. The response already carries the
 * series, so a trend is read off the buckets themselves - asking the API for a second window would invent a
 * comparison the report already answers, and the two could disagree.
 *
 * Shared by the dashboard summary and the detail screen so both fetch identically.
 */
export function useRecurringRevenue(
  filters: RecurringRevenueFiltersInput,
  timeAggregationPeriod: PaymentConversionPeriod = 'MONTH',
) {
  const toast = useToastContext();
  const [data, setData] = useState<RecurringRevenueResponse>(emptyResponse);
  const [isLoading, setIsLoading] = useState(false);
  /** A request that FAILED, not one that answered with nothing - see ReportUnavailable. */
  const [hasFailed, setHasFailed] = useState(false);
  /**
   * Whether a response has ever been APPLIED, which is a third state and not the absence of the other two.
   *
   * Before the first request - and permanently while no community is selected - `data` is `emptyResponse` with
   * neither loading nor failure set. Once a missing population stopped being zero-filled, that idle state
   * started reading as "the report could not measure its coverage": a warning about a report nobody asked for
   * yet. Idle is not an answer, so the screens must be able to tell it from one. Codex, #267.
   */
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    // Guards against a superseded request: changing communities or period fires a new one while the old is
    // still in flight, and without this the slower response wins whichever it is.
    let active = true;

    if (!filters.botConfigIds?.length) {
      setData(emptyResponse);
      setHasFailed(false);
      // Back to idle: deselecting every community un-answers the question rather than answering it with zero.
      setHasLoaded(false);
      // Cleared explicitly: deselecting every community invalidates the in-flight request, so its finally
      // no longer runs and the card would spin forever instead of showing the empty state.
      setIsLoading(false);
      return () => {
        active = false;
      };
    }

    const parsed = parseFilters(filters);

    setIsLoading(true);
    findRecurringRevenue({
      botConfigIds: parsed.botConfigIds,
      clientTz: parsed.clientTz,
      userRoles: parsed.userRoles,
      startDate: parsed.startDate,
      endDate: parsed.endDate,
      timeAggregationPeriod,
    })
      .then((response) => {
        if (!active) return;

        setHasFailed(false);
        setHasLoaded(true);
        const payload = response.data ?? emptyResponse;
        setData({
          snapshot: { ...emptySnapshot, ...(payload.snapshot ?? {}) },
          // Merged field by field like the others: a coverage object present but partial would defeat a spread,
          // and `pricedFromPeriods` missing must read false rather than undefined.
          coverage: {
            ...emptyCoverage,
            ...(payload.coverage ?? {}),
            opening: {
              ...emptyCoverage.opening,
              ...(payload.coverage?.opening ?? {}),
            },
            current: {
              ...emptyCoverage.current,
              ...(payload.coverage?.current ?? {}),
            },
          },
          rows: (payload.rows ?? []).map(normaliseRow),
          totals: { ...emptyTotals, ...(payload.totals ?? {}) },
        });
      })
      .catch((error) => {
        if (!active) return;

        setData(emptyResponse);
        setHasFailed(true);
        // A refusal is not an answer either, and `hasFailed` is what the screens say about it.
        setHasLoaded(false);
        toast.handleError(error, 'Não foi possível carregar os registros');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [filters, timeAggregationPeriod]);

  return {
    snapshot: data.snapshot,
    coverage: data.coverage,
    rows: data.rows,
    totals: data.totals,
    isLoading,
    hasFailed,
    hasLoaded,
  };
}
